import { useCallback, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ItemCreationSection } from '~/Components';
import { REQUEST_ACTIONS } from '~/Constants/Actions.constants';
import { isObjectEmpty } from '~/Helpers/CleanData.helper';
import { useNavigationContext, useProvider } from '~/Hooks';
import { IVODRequestParams } from '~/Models/DTO/VideoOnDemand.models';
import { useRequestCreateVOD } from '~/requests/videoOnDemandService/useRequestCreateVOD';
import { useRequestGetVideoById } from '~/requests/videoOnDemandService/useRequestGetVideoById';
import { selectLoading } from '~/store/app';
import { ExtendedVoD } from './ExtendedVoD.model';
import { VideoOnDemandDetailsForm } from './VideoOnDemandDetailsForm';
import { vodVerification } from './videoVerification';

const _initialValues: ExtendedVoD = {
  name: '',
  categoryId: 0,
  state: 'online',
  trainerIds: [],
  language: 'en',
  descriptions: {
    EN: '',
    DE: '',
    ES: '',
    PT: '',
    FE: '',
    NL: '',
  },
  imageUrl: '',
};

interface IUseVideoOnDemandEdit {
  id?: string;
}
export const useVideoOnDemandEdit = ({ id }: IUseVideoOnDemandEdit = {}) => {
  const { currentProvider } = useProvider();
  const { blockNavigation } = useNavigationContext();
  const { getVideoById, videoData } = useRequestGetVideoById(id);

  // Fetching and mutation hooks
  // const { data: video, isLoading: isVideoLoading, error: videoError } = useRequestVideo(id);
  // const {
  //   mutate: updateVideo,
  //   isSuccess: isVideoUpdateSuccess,
  //   error: videoUpdateError,
  //   isLoading: isVideoUpdateLoading,
  // } = useRequestVideoUpdate();

  const {
    isVodVideoCreateLoading,
    isVodVideoCreateSuccess,
    vodCreateResponse,
    vodCreateError,
    createVideo,
  } = useRequestCreateVOD();

  // Store selectors
  const imageUploading = useSelector(selectLoading(REQUEST_ACTIONS.UPLOAD_IMAGE));

  // State
  const [initialValues, setInitialValues] = useState<ExtendedVoD>(_initialValues);
  const [currentVideo, setCurrentVideo] = useState<ExtendedVoD>(_initialValues);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  const saveVideoFormData = useCallback((video: ExtendedVoD) => setInitialValues(video), []);

  const validate = useCallback((validateVideo: ExtendedVoD) => {
    const errors = vodVerification(validateVideo);
    const shouldDisable = !isObjectEmpty(errors);
    setIsSubmitDisabled(shouldDisable);
    setCurrentVideo(validateVideo);
    return errors;
  }, []);

  //Retrive video data for Editing
  useEffect(() => {
    if (id) {
      getVideoById();
    }
  }, [id]);

  //Aftercreating video, setting video id for thumbnail upload
  useEffect(() => {
    if (vodCreateResponse?.id) {
      const video = {
        ...currentVideo,
        id: vodCreateResponse?.id,
      } as any;
      saveVideoFormData(video);
      validate(video);
    }
  }, [vodCreateResponse?.id]);

  //Setting video Data for Editing
  useEffect(() => {
    if (videoData?.id && id) {
      const video = {
        ...videoData,
        state: videoData?.releaseDate ? 'online' : 'draft',
      } as any;
      saveVideoFormData(video);
      validate(video);
    }
  }, [videoData?.id, id]);

  const onSave = useCallback(
    async (filename: string) => {
      const payload = { ...currentVideo };
      if (currentVideo) {
        if (id) {
          // TODO: updateVideo(payload as IVODRequestParams);
        } else {
          createVideo({ ...payload, filename } as IVODRequestParams);
        }
        blockNavigation(false);
      }
    },
    [currentVideo, id, currentProvider],
  );

  // Item creation sections (for ItemCreation component)
  const sections: ItemCreationSection<ExtendedVoD>[] = useMemo(
    () => [({ data }) => VideoOnDemandDetailsForm(data)],
    [videoData],
  );

  return {
    id,
    listItems: sections,
    onSave,
    currentVideo,
    initialValues,
    validate,
    error: vodCreateError || {},
    isLoading: isVodVideoCreateLoading || false,
    isSubmitDisabled: isSubmitDisabled || hasValidationErrors || imageUploading,
    vodCreateResponse,
    isVodVideoCreateSuccess,
    onValidationErrors: setHasValidationErrors,
    saveVideoFormData,
    thumbnailUrl: videoData?.thumbnailUrl,
  };
};
